/**
 * Skalerer et billede ned, før det sendes.
 *
 * Et billede fra en telefon kan fylde 30 MB og være 6000 px bredt. I en mail vises det aldrig bredere end
 * 560 px, så det store billede gør kun mailen langsom - og serveren afviser det. Her skaleres det til en
 * fornuftig bredde i browseren først, så den, der skriver mailen, ikke skal tænke over filstørrelser.
 *
 * Billeder, der allerede er små nok, sendes uændret - så mister et skarpt logo ikke kvalitet uden grund.
 */

const MAX_WIDTH = 1200;
const SEND_AS_IS_BYTES = 400 * 1024;

const readImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Billedet kunne ikke læses"));
    };
    image.src = url;
  });

export const resizeImage = async (file: File): Promise<File> => {
  // PNG beholder vi som PNG, så gennemsigtighed ikke bliver til en sort kant
  const keepsTransparency = file.type === "image/png";
  if (file.size <= SEND_AS_IS_BYTES) return file;

  try {
    const image = await readImage(file);
    if (image.width <= MAX_WIDTH && file.size <= 2 * 1024 * 1024) return file;

    const scale = Math.min(1, MAX_WIDTH / image.width);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(image.width * scale);
    canvas.height = Math.round(image.height * scale);
    const context = canvas.getContext("2d");
    if (!context) return file;
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    const type = keepsTransparency ? "image/png" : "image/jpeg";
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, 0.85));
    if (!blob || blob.size >= file.size) return file;

    const name = file.name.replace(/\.[^.]+$/, "") + (keepsTransparency ? ".png" : ".jpg");
    return new File([blob], name, { type });
  } catch {
    // Kan billedet ikke læses her, sendes det som det er - så svarer serveren på det
    return file;
  }
};
